import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import infoIcon from '@/assets/icons/info.svg';

// services
import { accountingCenterService } from '@/services/accounting-centers.service';

// utils
import { calculateChartData, getChartConfigs } from '@/utils/chartCalculations';

// components
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import PieGraph from '@/components/organisms/graphs/PieGraph';
import InfoModal from '@/components/molecules/InfoModal';

// icons
import departmentACIcon from '@/assets/icons/department-acs.svg';
import noInvoiceIcon from '@/assets/icons/no-invoice.svg';
import binIcon from '@/assets/icons/bin.svg';
import editIcon from '@/assets/icons/edit.svg';
import percentageIcon from '@/assets/icons/percentage.svg';
import euroIcon from '@/assets/icons/euro.svg';
import invoiceIcon from '@/assets/icons/invoice.svg';
import { setBreadcrumbs } from '@/store/slices/breadcrumb';
import { useDispatch } from 'react-redux';
import deleteBankAccountIcon from '@/assets/icons/delete-bankAccount.svg';
import ExplanatoryModal from '@/components/molecules/ExplanatoryModal';

import acExpanatoryImg from '@/assets/images/ac-explanatory-1.png';
import acExpanatoryImg2 from '@/assets/images/ac-explanatory-2.png';
import acExpanatoryImg3 from '@/assets/images/ac-explanatory-3.png';

const steps = [
  {
    title: 'Understanding MAEs',
    message:
      'MAEs are customizable entities designed for better cashflow management. They are categorized into four types: Departments, Products & Services, Projects, and Contract Entities.',
    img: acExpanatoryImg
  },
  {
    title: 'Setting Limits with ACMEV and ACRT',
    message:
      'ACMEV is the maximum spending limit for each AC. ACRT is the revenue target assigned to each AC. Both values are aligned with the overall BMEV and BTRT.',
    img: acExpanatoryImg2
  },
  {
    title: 'Linking Invoices to MAEs',
    message:
      'Invoices can be allocated to specific MAEs, providing detailed insights into the financial activities of each category.',
    img: acExpanatoryImg3
  }
];

const ACDetailPage = () => {
  const [accountingCenter, setAccountingCenter] = useState<any>(null);
  const [graphViewMode, setGraphViewMode] = useState('percentage');
  const { acId, budgetId, acType } = useParams();
  const [isExpanded, setIsExpanded] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [explanatoryModal, setExplanatoryModal] = useState(true);

  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const acCenter = location.state?.acCenter as any | undefined;

  console.log('acType', acType);
  console.log('budgetId', budgetId);
  console.log('acId', acId);

  useEffect(() => {
    // Set breadcrumbs when component mounts
    dispatch(
      setBreadcrumbs([
        {
          title: 'Budget',
          path: `/budgets/${budgetId}`
        },
        {
          title: acCenter?.name ? acCenter?.name + ' Overview' : ' Accounting Center Overview',
          path: ``
        },
        {
          title: acCenter?.name ? acCenter?.name + '' : ' Accounting Center',
          path: ``
        }
      ])
    );
  }, [dispatch, budgetId]);

  const fetchAccountingCenter = async () => {
    try {
      const response = await accountingCenterService.getAccountingCenter(budgetId || '', acId || '');
      setAccountingCenter(response.data);
    } catch (error) {
      console.error('Error fetching accounting center:', error);
    }
  };

  const { acmevChartData, acrtChartData } = calculateChartData(accountingCenter, '#7663EB');
  const { acmevChartConfig, acrtChartConfig } = getChartConfigs('primary');

  useEffect(() => {
    fetchAccountingCenter();
  }, []);

  const handleDeleteBankAccount = async () => {
    try {
      await accountingCenterService.deleteAccountingCenter(budgetId || '', acId || '');
      toast.success('Accounting center deleted successfully');
      navigate(-1);
    } catch (error) {
      console.error('Error deleting accounting center:', error);
    }
  };

  return (
    <div className="flex-1 flex flex-col gap-4">
      <div className="flex gap-4">
        <div className="md:border flex flex-col flex-1 p-4 md:bg-white md:rounded-[16px]">
          <header className="">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={departmentACIcon} alt="budget-icon" width={40} height={40} />
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    navigate(`/budgets/${budgetId}/accounting-centers/${acType}/edit/${acId}`, {
                      state: { acCenter }
                    });
                  }}
                  title="Edit"
                >
                  <img src={editIcon} alt="edit-icon" width={28} height={28} />
                </button>
                <button
                  title="Info"
                  onClick={() => {
                    setCurrentStep(0);
                    setIsOpen(true);
                  }}
                >
                  <img className="w-[28px] h-[28px]" src={infoIcon} alt="vault icon" />
                </button>
                <button type="button" onClick={() => setDeleteConfirmModal(true)} title="Delete">
                  <img src={binIcon} alt="bin-icon" width={28} height={28} />
                </button>
              </div>
            </div>
            <p className="text-[24px] font-semibold mt-2">{accountingCenter?.name}</p>
          </header>

          <div className="border border-primary rounded-[16px] p-4 bg-lightPurple flex-1 space-y-3 mt-3">
            <p className="text-primary font-bold text-base mb-3">General Information</p>

            <div>
              <p className="text-sm text-secondary font-medium leading-[18px]">Description</p>
              <p className={`text-base font-medium leading-[24.5px] ${isExpanded ? '' : 'line-clamp-2'}`}>
                {accountingCenter?.description || 'No description provided'}
              </p>
              {!isExpanded && accountingCenter?.description && accountingCenter?.description?.length > 40 && (
                <button className="text-sm text-primary" onClick={() => setIsExpanded(true)}>
                  See More
                </button>
              )}
              {isExpanded && (
                <button className="text-sm text-primary" onClick={() => setIsExpanded(false)}>
                  See Less
                </button>
              )}
            </div>

            <div className="flex gap-4">
              <div className="flex-1">
                <p className="text-sm text-secondary font-medium leading-[18px]">ACMEV</p>
                <p className="text-base font-medium leading-[24.5px]">€{accountingCenter?.acmev?.toLocaleString()}</p>
              </div>

              <div className="flex-1">
                <p className="text-sm text-secondary font-medium leading-[18px]">ACMEV Alerting Threshold</p>
                <p className="text-base font-medium leading-[24.5px]">{accountingCenter?.acmev_alert_threshold}%</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-1">
                <p className="text-sm text-secondary font-medium leading-[18px]">ACRT</p>
                <p className="text-base font-medium leading-[24.5px]">€{accountingCenter?.acrt?.toLocaleString()}</p>
              </div>

              <div className="flex-1">
                <p className="text-sm text-secondary font-medium leading-[18px]">ACRT Alerting Threshold</p>
                <p className="text-base font-medium leading-[24.5px]">{accountingCenter?.acrt_alert_threshold}%</p>
              </div>
            </div>
          </div>
        </div>
        {/* <div className="border min-w-[418px] p-4"> */}
        {/* GRAPH OVERVIEW */}
        <div className="md:w-[40%] md:bg-white md:p-[0.85rem] md:rounded-[16px] md:border">
          <header className="w-full flex items-center justify-between mb-3 md:mb-4">
            <h3 className="text-base font-bold text-primary md:text-foreground">Graph Overview</h3>
            <Tabs defaultValue="percentage" value={graphViewMode} onValueChange={setGraphViewMode}>
              <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                <TabsTrigger value="percentage" className="group md:py-[0.3rem]">
                  <img
                    src={percentageIcon}
                    alt="percentage-icon"
                    width={13}
                    height={17}
                    className="group-data-[state=active]:brightness-[100]"
                  />
                </TabsTrigger>
                <TabsTrigger value="euro" className="group md:py-[0.3rem]">
                  <img
                    src={euroIcon}
                    alt="euro-icon"
                    width={12}
                    height={12}
                    className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                  />
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </header>
          <div className="flex items-start justify-between gap-3">
            <PieGraph
              title="Remaining ACMEV"
              titleValue={(() => {
                if (graphViewMode === 'percentage' && acmevChartData) {
                  return acmevChartData.length > 0 ? `${acmevChartData[0].value}%` : '0%';
                } else {
                  const acmevTotal = accountingCenter?.acmev || 0;
                  const acmevUsed = accountingCenter?.acmev_used || 0;
                  const acmevRemaining = Math.max(0, acmevTotal - acmevUsed);
                  return `€${acmevRemaining.toLocaleString()}`;
                }
              })()}
              chartConfig={acmevChartConfig}
              chartData={acmevChartData || []}
              className="bg-white rounded-[16px] p-[0.85rem] flex-1 md:border border-lightGray md:py-6 md:flex md:items-center md:justify-center md:flex-col"
              minHeight={140}
              outerRadius={64}
            />

            <PieGraph
              title="Achieved ACRT"
              titleValue={(() => {
                if (graphViewMode === 'percentage') {
                  const achievedItem = acrtChartData && acrtChartData.find((item) => item.key === 'achieved');
                  return achievedItem ? `${achievedItem.value}%` : '0%';
                } else {
                  const acrtAchieved = accountingCenter?.acrt_achieved || 0;
                  return `€${acrtAchieved.toLocaleString()}`;
                }
              })()}
              chartConfig={acrtChartConfig}
              chartData={acrtChartData || []}
              variant="primary"
              className="bg-white rounded-[16px] p-[0.85rem] flex-1 md:border border-lightGray md:py-6 md:flex md:items-center md:justify-center md:flex-col"
              minHeight={140}
              outerRadius={64}
            />
          </div>
        </div>
        {/* </div> */}
      </div>
      <div className="flex-1 flex flex-col border bg-white rounded-[16px] p-4">
        <header>
          <h4 className="flex items-center gap-2">
            <img src={invoiceIcon} alt="invoice-icon" />
            <span className="font-bold">Recent Invoice</span>
          </h4>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <div className="flex items-center gap-2">
            <img className="w-[40px] h-[40px]" src={noInvoiceIcon} alt="no-invoice-icon" />
            <div className="flex flex-col items-start justify-center">
              <p className="font-medium leading-[140%]">There are no Invoice to show</p>
              <span className="text-secondary text-sm leading-[125%]">
                Please create a new invoice to allocate budget!
              </span>
            </div>
          </div>
          <Button variant={'primary-reverse'} className="rounded-full px-6">
            Create an Invoice
          </Button>
        </div>
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirmModal && (
        <InfoModal
          isOpen={deleteConfirmModal}
          variant="confirm"
          title="Are you sure want to Delete this Accounting Center?"
          message={<p>Deleted Department ACs will removed permanently and unable to restore</p>}
          buttonText="Delete"
          onButtonClick={handleDeleteBankAccount}
          onClose={() => setDeleteConfirmModal(false)}
          icon={deleteBankAccountIcon}
        />
      )}

      {/* Explanatory Modal */}
      {isOpen && (
        <ExplanatoryModal
          isOpen={isOpen}
          steps={steps}
          currentStep={currentStep}
          buttonText="Next"
          onButtonClick={() => {
            setCurrentStep(currentStep + 1);
          }}
          onClose={() => {
            setIsOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default ACDetailPage;
